import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import type { ThreatReport } from '@/services/threat-intelligence.service';

interface ReportsListProps {
  reports: ThreatReport[];
  isLoading: boolean;
  onPreviewReport: (reportId: string) => void;
  onDownloadReport: (reportId: string, filename: string) => void;
  onDeleteReport: (reportId: string) => void;
}

function ReportCard({
  report,
  onPreview,
  onDownload,
  onDelete
}: {
  report: ThreatReport;
  onPreview: () => void;
  onDownload: () => void;
  onDelete: () => void;
}) {
  const formatColors = {
    pdf: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
    excel: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    csv: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    html: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) {return '0 Bytes';}
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {return 'Just now';}
    if (diffInHours < 24) {return `${diffInHours}h ago`;}
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {return `${diffInDays}d ago`;}

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Card hover="default" className="transition-all duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <DocumentTextIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                {report.title}
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {report.reportNumber}
              </p>
            </div>
          </div>
          <span className={cn('ml-4 px-2 py-1 text-xs font-medium rounded-full flex-shrink-0', formatColors[report.format])}>
            {report.format.toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
              <CalendarIcon className="w-4 h-4" />
              <span className="text-xs">Generated:</span>
            </div>
            <span className="text-sm font-medium text-secondary-900 dark:text-white">
              {formatDate(report.generatedAt)}
            </span>
          </div>
          <div>
            <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
              <ExclamationTriangleIcon className="w-4 h-4" />
              <span className="text-xs">Threats:</span>
            </div>
            <span className="text-sm font-medium text-secondary-900 dark:text-white">
              {report.totalThreats} total
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {report.criticalThreats > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300 rounded">
              {report.criticalThreats} Critical
            </span>
          )}
          {report.highThreats > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300 rounded">
              {report.highThreats} High
            </span>
          )}
          {report.mediumThreats > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300 rounded">
              {report.mediumThreats} Medium
            </span>
          )}
          {report.lowThreats > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300 rounded">
              {report.lowThreats} Low
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-secondary-200 dark:border-secondary-700">
          <div className="text-xs text-secondary-500 dark:text-secondary-400">
            {formatBytes(report.fileSize)}
            {report.pageCount && ` • ${report.pageCount} pages`}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onPreview}
              className="text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <EyeIcon className="w-4 h-4 mr-1" />
              Preview
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDownload}
              className="text-secondary-600 hover:text-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-800"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              Download
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsList({
  reports,
  isLoading,
  onPreviewReport,
  onDownloadReport,
  onDeleteReport,
}: ReportsListProps) {
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-secondary-600 dark:text-secondary-400">Loading reports...</p>
        </CardContent>
      </Card>
    );
  }

  if (reports.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <DocumentTextIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
            No reports generated yet
          </h3>
          <p className="text-secondary-600 dark:text-secondary-400">
            Click &quot;Generate Report&quot; to create your first threat intelligence report.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {reports.map((report) => (
        <motion.div
          key={report.id}
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          layout
        >
          <ReportCard
            report={report}
            onPreview={() => onPreviewReport(report.id)}
            onDownload={() => {
              const filename = `${report.reportNumber.replace(/[^a-zA-Z0-9-]/g, '_')}.${report.format === 'excel' ? 'xlsx' : report.format}`;
              onDownloadReport(report.id, filename);
            }}
            onDelete={() => onDeleteReport(report.id)}
          />
        </motion.div>
      ))}
    </div>
  );
}
